import { useCallback, useRef, ReactNode } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { Checkbox } from './Checkbox';

export interface DataTableColumn<T> {
  key: string;
  title: string;
  width?: number | string;
  sortable?: boolean;
  render?: (row: T) => ReactNode;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  getRowId: (row: T) => string;
  selectable?: boolean;
  selected?: ReadonlySet<string>;
  onSelectedChange?: (selected: Set<string>) => void;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  onSortChange?: (key: string, dir: 'asc' | 'desc') => void;
  /** fires when the sentinel row below the last row becomes visible (endless scroll) */
  onEndReached?: () => void;
  emptyState?: ReactNode;
}

// Hoisted so the `selected` default doesn't allocate a fresh Set on every
// render — the component doesn't currently memoize on it, but a stable
// reference keeps that safe if memoization is added later.
const EMPTY_SELECTED: ReadonlySet<string> = new Set<string>();

export function DataTable<T>({
  columns,
  rows,
  getRowId,
  selectable = false,
  selected = EMPTY_SELECTED,
  onSelectedChange,
  sortBy,
  sortDir = 'asc',
  onSortChange,
  onEndReached,
  emptyState,
}: DataTableProps<T>) {
  // A callback ref (rather than useRef + useEffect keyed on [onEndReached])
  // so the observer attaches whenever the sentinel node actually mounts —
  // including the common empty-state -> populated-rows transition, where
  // the sentinel doesn't exist on the first render and a dependency array
  // keyed only on `onEndReached` would never re-run.
  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      if (!node || !onEndReached) return;
      const observer = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) onEndReached();
      });
      observer.observe(node);
      observerRef.current = observer;
    },
    [onEndReached]
  );

  const allIds = rows.map(getRowId);
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));
  const someSelected = allIds.some((id) => selected.has(id));

  const toggleAll = () => {
    // Only add/remove ids that are actually in the current `rows` — never
    // touch ids selected from a previous page/filter that aren't in view,
    // or select-all/deselect-all would silently drop off-screen selections.
    const next = new Set(selected);
    if (allSelected) allIds.forEach((id) => next.delete(id));
    else allIds.forEach((id) => next.add(id));
    onSelectedChange?.(next);
  };

  const toggleOne = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectedChange?.(next);
  };

  if (rows.length === 0 && emptyState) return <>{emptyState}</>;

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            {selectable && (
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected && !allSelected}
                  onChange={toggleAll}
                  inputProps={{ 'aria-label': 'select all rows' }}
                />
              </TableCell>
            )}
            {columns.map((col) => (
              <TableCell
                key={col.key}
                sx={{ width: col.width, color: 'text.secondary', fontSize: 12, textTransform: 'uppercase' }}
                sortDirection={sortBy === col.key ? sortDir : false}
              >
                {col.sortable ? (
                  <TableSortLabel
                    active={sortBy === col.key}
                    direction={sortBy === col.key ? sortDir : 'asc'}
                    onClick={() =>
                      onSortChange?.(col.key, sortBy === col.key && sortDir === 'asc' ? 'desc' : 'asc')
                    }
                  >
                    {col.title}
                  </TableSortLabel>
                ) : (
                  col.title
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const id = getRowId(row);
            return (
              <TableRow key={id} hover selected={selected.has(id)}>
                {selectable && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selected.has(id)}
                      onChange={() => toggleOne(id)}
                      inputProps={{ 'aria-label': `select row ${id}` }}
                    />
                  </TableCell>
                )}
                {columns.map((col) => (
                  <TableCell key={col.key} sx={{ fontSize: 14 }}>
                    {col.render ? col.render(row) : String((row as Record<string, unknown>)[col.key] ?? '')}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {onEndReached && <div ref={sentinelRef} data-testid="data-table-sentinel" />}
    </TableContainer>
  );
}
